import styles from './experience-signals.module.scss';
import React, { FC, useEffect } from 'react';
import { ExperienceFeedbackDetail } from '../../../models/ai-signals/experience-insights';

interface ExperienceFeedbackTicketModalProps {
	feedbackId: string;
	detail: ExperienceFeedbackDetail | null;
	isLoading: boolean;
	errorMessage: string | null;
	onClose: () => void;
}

const sentimentClass = (sentiment?: string | null) => {
	const value = (sentiment || '').toLowerCase();
	if (value.includes('pos')) {
		return styles.tag_pos;
	}
	if (value.includes('neg')) {
		return styles.tag_neg;
	}
	return styles.tag_neu;
};

const churnColor = (score?: number | null) => {
	if (score == null) {
		return undefined;
	}
	if (score >= 8) {
		return '#d0334c';
	}
	if (score >= 5) {
		return '#b45309';
	}
	return '#0f766e';
};

const ExperienceFeedbackTicketModal: FC<ExperienceFeedbackTicketModalProps> = ({
	feedbackId,
	detail,
	isLoading,
	errorMessage,
	onClose,
}) => {
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	const isNumericId = /^\d+$/.test(feedbackId);
	const isPrefixedId = /^(EM|HC)-\d+$/i.test(feedbackId);
	const title = isNumericId || isPrefixedId
		? `Ticket #${feedbackId}`
		: `Review ${feedbackId.substring(0, 8)}…`;

	return (
		<div
			className={styles.overlay}
			onClick={(event) => {
				if (event.target === event.currentTarget) {
					onClose();
				}
			}}
		>
			<div className={styles.modal} role="dialog" aria-modal="true" aria-label={title}>
				<div className={styles.modal_head}>
					<h3>{title}</h3>
					<button type="button" className={styles.modal_close} onClick={onClose} aria-label="Close">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M3 3l8 8M11 3l-8 8" />
						</svg>
					</button>
				</div>
				<div className={styles.modal_body}>
					{isLoading ? (
						<div className={styles.spin} />
					) : errorMessage ? (
						<div className={styles.modal_error}>{errorMessage}</div>
					) : detail ? (
						<>
							<div className={styles.modal_summary}>
								{detail.sourcePlatform ? (
									<span className={`${styles.tag} ${styles.tag_src}`}>{detail.sourcePlatform}</span>
								) : null}
								{detail.channelName ? <span className={styles.meta_item}>{detail.channelName}</span> : null}
								{detail.feedbackDateDisplay ? (
									<>
										<span className={styles.meta_sep}>·</span>
										<span className={styles.meta_item}>{detail.feedbackDateDisplay}</span>
									</>
								) : null}
								{detail.customerName ? (
									<>
										<span className={styles.meta_sep}>·</span>
										<span className={styles.meta_item}>{detail.customerName}</span>
									</>
								) : null}
							</div>
							{detail.summary ? <div className={styles.subject_line}>{detail.summary}</div> : null}
							{detail.body ? <div className={styles.feedback_full}>{detail.body}</div> : null}
							<div className={styles.field_row}>
								<div className={styles.field_label}>Product</div>
								<div className={styles.field_value} style={{ fontWeight: 500 }}>
									{detail.productName || 'Unknown'}
								</div>
							</div>
							<div className={styles.field_row}>
								<div className={styles.field_label}>Category</div>
								<div className={styles.field_value}>
									{detail.categoryName ? (
										<span className={`${styles.tag} ${sentimentClass(detail.sentimentCategory)}`}>
											{detail.categoryName}
										</span>
									) : (
										'—'
									)}
								</div>
							</div>
							<div className={styles.field_row}>
								<div className={styles.field_label}>Sentiment</div>
								<div className={styles.field_value}>
									{detail.sentimentCategory ? (
										<span className={`${styles.tag} ${sentimentClass(detail.sentimentCategory)}`}>
											{detail.sentimentCategory}
										</span>
									) : (
										'—'
									)}
								</div>
							</div>
							<div className={styles.field_row}>
								<div className={styles.field_label}>Churn Risk</div>
								<div
									className={styles.field_value}
									style={{ fontWeight: 600, color: churnColor(detail.churnRiskScore10) }}
								>
									{detail.churnRiskScore10 != null ? `${detail.churnRiskScore10} / 10` : '—'}
								</div>
							</div>
							<div className={styles.field_row}>
								<div className={styles.field_label}>Root Cause</div>
								<div className={styles.field_value}>{detail.rootCause || '—'}</div>
							</div>
						</>
					) : (
						<div className={styles.modal_error}>Feedback details unavailable.</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ExperienceFeedbackTicketModal;
